/**
 * useGameEngine - GameEngine用Reactアダプター
 *
 * GameEngineの純粋関数をReactコンポーネントから使用するためのカスタムフック。
 * 既存のmagic-spirit.jsxとの互換性を保ちながら段階的に移行可能。
 */

import { useState, useCallback, useMemo } from 'react';
import {
  createInitialState,
  applyAction,
  actions,
  PHASES,
  GAME_STATUS,
  getPlayer,
  getStateSummary,
} from './index';
import type {
  GameState,
  PlayerState,
  CreateInitialStateConfig,
  LogEntry,
  GameStatusType,
  PhaseType,
  StateSummary,
} from './GameState';
import type { GameAction } from './GameActions';
import type { PlayerNumber } from '../../types/player';
import type { CardWithId, MonsterInstance, PhaseCardInstance, FieldCardInstance, CardData } from '../../types/card';
import type { StatusEffect } from '../../types/effect';
import { createDeepSeaZone } from '../deepSeaZone';
import type { DeepSeaZone } from '../deepSeaZone/types';

/** 既存形式の状態オブジェクト */
export interface LegacyState {
  turn: number;
  currentPlayer: PlayerNumber;
  phase: PhaseType;
  isFirstTurn: boolean;
  winner: PlayerNumber | null;
  logs: LogEntry[];
  // P1
  p1Life: number;
  p1Deck: CardWithId[];
  p1Hand: CardWithId[];
  p1Field: (MonsterInstance | null)[];
  p1Graveyard: CardWithId[];
  p1ActiveSP: number;
  p1RestedSP: number;
  p1FieldCard: FieldCardInstance | CardData | null;
  p1PhaseCard: PhaseCardInstance | null;
  p1StatusEffects: StatusEffect[];
  p1NextTurnSPBonus: number;
  p1MagicBlocked: boolean;
  p1SpReduction: number;
  p1DeepSeaZone?: DeepSeaZone;
  // P2
  p2Life: number;
  p2Deck: CardWithId[];
  p2Hand: CardWithId[];
  p2Field: (MonsterInstance | null)[];
  p2Graveyard: CardWithId[];
  p2ActiveSP: number;
  p2RestedSP: number;
  p2FieldCard: FieldCardInstance | CardData | null;
  p2PhaseCard: PhaseCardInstance | null;
  p2StatusEffects: StatusEffect[];
  p2NextTurnSPBonus: number;
  p2MagicBlocked: boolean;
  p2SpReduction: number;
  p2DeepSeaZone?: DeepSeaZone;
  // ターンフラグ
  chargeUsedThisTurn: boolean;
}

/** useGameEngineの戻り値型 */
export interface UseGameEngineReturn {
  // 状態
  state: GameState | null;
  isInitialized: boolean;
  // ゲーム進行
  turn: number;
  currentPlayer: PlayerNumber;
  phase: PhaseType;
  isFirstTurn: boolean;
  winner: PlayerNumber | null;
  gameStatus: GameStatusType;
  logs: LogEntry[];
  // プレイヤー状態
  p1: PlayerState | null;
  p2: PlayerState | null;
  currentPlayerData: PlayerState | null;
  opponentData: PlayerState | null;
  // アクション
  dispatch: (action: GameAction) => void;
  dispatchMultiple: (actionList: GameAction[]) => void;
  initGame: (config: CreateInitialStateConfig) => GameState;
  resetGame: () => void;
  syncFromLegacy: (legacyState: LegacyState) => void;
  // アクションラッパー
  summonCard: (cardIndex: number, slotIndex: number) => void;
  attack: (attackerIndex: number, targetIndex: number) => void;
  chargeCard: (handCardIndex: number, targetSlotIndex: number) => void;
  chargeSP: (targetSlotIndex: number) => void;
  executeSkill: (monsterIndex: number, skillType: 'basic' | 'advanced') => void;
  useMagic: (cardIndex: number) => void;
  processPhase: (phaseIndex: PhaseType) => void;
  nextPhase: () => void;
  endTurn: () => void;
  // ユーティリティ
  summary: StateSummary | null;
  actions: typeof actions;
  PHASES: typeof PHASES;
  GAME_STATUS: typeof GAME_STATUS;
}

/**
 * GameEngineカスタムフック
 */
export function useGameEngine(): UseGameEngineReturn {
  // GameStateを1つのuseStateで管理
  const [engineState, setEngineState] = useState<GameState | null>(null);

  /**
   * アクションをディスパッチして状態を更新
   */
  const dispatch = useCallback((action: GameAction): void => {
    setEngineState((prevState: GameState | null): GameState | null => {
      if (!prevState) return prevState;
      return applyAction(prevState, action);
    });
  }, []);

  /**
   * 複数アクションを順次適用
   */
  const dispatchMultiple = useCallback((actionList: GameAction[]): void => {
    setEngineState((prevState: GameState | null): GameState | null => {
      if (!prevState) return prevState;
      let newState: GameState = prevState;
      for (const action of actionList) {
        newState = applyAction(newState, action);
      }
      return newState;
    });
  }, []);

  /**
   * ゲーム初期化
   */
  const initGame = useCallback((config: CreateInitialStateConfig): GameState => {
    // configをそのままcreateInitialStateに渡す（summoningSickness, secondPlayerBonusSP等を含む）
    const initialState = createInitialState(config);
    setEngineState(initialState);
    return initialState;
  }, []);

  /**
   * ゲームリセット
   */
  const resetGame = useCallback((): void => {
    setEngineState(null);
  }, []);

  /**
   * 既存のuseStateからエンジン状態を同期
   * Phase A-3: 段階的移行用 - useStateの値をengineStateに反映
   */
  const syncFromLegacy = useCallback((legacyState: LegacyState): void => {
    if (!legacyState) return;
    const newState = fromLegacyState(legacyState);
    setEngineState(newState);
  }, []);

  // ========================================
  // 便利なアクションラッパー
  // ========================================

  const summonCard = useCallback((cardIndex: number, slotIndex: number): void => {
    dispatch(actions.summonCard(cardIndex, slotIndex));
  }, [dispatch]);

  const attack = useCallback((attackerIndex: number, targetIndex: number): void => {
    dispatch(actions.attack(attackerIndex, targetIndex));
  }, [dispatch]);

  const chargeCard = useCallback((handCardIndex: number, targetSlotIndex: number): void => {
    dispatch(actions.chargeCard(handCardIndex, targetSlotIndex));
  }, [dispatch]);

  const chargeSP = useCallback((targetSlotIndex: number): void => {
    dispatch(actions.chargeSP(targetSlotIndex));
  }, [dispatch]);

  const executeSkill = useCallback((monsterIndex: number, skillType: 'basic' | 'advanced'): void => {
    dispatch(actions.executeSkill(monsterIndex, skillType));
  }, [dispatch]);

  const useMagic = useCallback((cardIndex: number): void => {
    dispatch(actions.useMagic(cardIndex));
  }, [dispatch]);

  const processPhase = useCallback((phaseIndex: PhaseType): void => {
    dispatch(actions.processPhase(phaseIndex));
  }, [dispatch]);

  const nextPhase = useCallback((): void => {
    dispatch(actions.nextPhase());
  }, [dispatch]);

  const endTurn = useCallback((): void => {
    dispatch(actions.endTurn());
  }, [dispatch]);

  // ========================================
  // 状態アクセサ（既存コードとの互換用）
  // ========================================

  const state: GameState | null = engineState;

  // プレイヤー状態を個別に取得（互換性用）
  const p1: PlayerState | null = state?.p1 ?? null;
  const p2: PlayerState | null = state?.p2 ?? null;

  // 現在のプレイヤー状態
  const currentPlayerData: PlayerState | null = state ? getPlayer(state, state.currentPlayer) : null;
  const opponentData: PlayerState | null = state ? getPlayer(state, state.currentPlayer === 1 ? 2 : 1) : null;

  // ゲーム進行状態
  const turn: number = state?.turn ?? 1;
  const currentPlayer: PlayerNumber = state?.currentPlayer ?? 1;
  const phase: PhaseType = state?.phase ?? 0;
  const isFirstTurn: boolean = state?.isFirstTurn ?? true;
  const winner: PlayerNumber | null = state?.winner ?? null;
  const gameStatus: GameStatusType = state?.gameStatus ?? GAME_STATUS.PLAYING;
  const logs: LogEntry[] = state?.logs ?? [];

  // デバッグ用サマリー
  const summary = useMemo((): StateSummary | null => {
    return state ? getStateSummary(state) : null;
  }, [state]);

  return {
    // 状態
    state: engineState,
    isInitialized: engineState !== null,

    // ゲーム進行
    turn,
    currentPlayer,
    phase,
    isFirstTurn,
    winner,
    gameStatus,
    logs,

    // プレイヤー状態
    p1,
    p2,
    currentPlayerData,
    opponentData,

    // アクション
    dispatch,
    dispatchMultiple,
    initGame,
    resetGame,
    syncFromLegacy,

    // アクションラッパー
    summonCard,
    attack,
    chargeCard,
    chargeSP,
    executeSkill,
    useMagic,
    processPhase,
    nextPhase,
    endTurn,

    // ユーティリティ
    summary,
    actions, // アクションクリエイター直接アクセス
    PHASES,
    GAME_STATUS,
  };
}

/**
 * GameStateから既存形式の状態オブジェクトを生成
 * magic-spirit.jsxの既存コードとの互換用
 */
export function toLegacyState(state: GameState | null): LegacyState | null {
  if (!state) return null;

  return {
    // ゲーム進行
    turn: state.turn,
    currentPlayer: state.currentPlayer,
    phase: state.phase,
    isFirstTurn: state.isFirstTurn,
    winner: state.winner,
    logs: state.logs,

    // P1
    p1Life: state.p1.life,
    p1Deck: state.p1.deck,
    p1Hand: state.p1.hand,
    p1Field: state.p1.field,
    p1Graveyard: state.p1.graveyard,
    p1ActiveSP: state.p1.activeSP,
    p1RestedSP: state.p1.restedSP,
    p1FieldCard: state.p1.fieldCard,
    p1PhaseCard: state.p1.phaseCard,
    p1StatusEffects: state.p1.statusEffects,
    p1NextTurnSPBonus: state.p1.nextTurnSPBonus,
    p1MagicBlocked: state.p1.magicBlocked,
    p1SpReduction: state.p1.spReduction,

    // P2
    p2Life: state.p2.life,
    p2Deck: state.p2.deck,
    p2Hand: state.p2.hand,
    p2Field: state.p2.field,
    p2Graveyard: state.p2.graveyard,
    p2ActiveSP: state.p2.activeSP,
    p2RestedSP: state.p2.restedSP,
    p2FieldCard: state.p2.fieldCard,
    p2PhaseCard: state.p2.phaseCard,
    p2StatusEffects: state.p2.statusEffects,
    p2NextTurnSPBonus: state.p2.nextTurnSPBonus,
    p2MagicBlocked: state.p2.magicBlocked,
    p2SpReduction: state.p2.spReduction,

    // ターンフラグ
    chargeUsedThisTurn: state.turnFlags.chargeUsedThisTurn,
  };
}

/**
 * 既存形式の状態からGameStateを生成
 * 既存コードからの移行用
 */
export function fromLegacyState(legacyState: LegacyState): GameState {
  return {
    config: {
      summoningSickness: true,
      secondPlayerBonusSP: true,
      piercingDamage: false,
      loneWarriorRule: false,
      initialLifePoints: 6000,
    },
    firstPlayer: 1,
    turn: legacyState.turn,
    currentPlayer: legacyState.currentPlayer,
    phase: legacyState.phase,
    isFirstTurn: legacyState.isFirstTurn,
    winner: legacyState.winner,
    gameStatus: legacyState.winner ? GAME_STATUS.GAME_OVER : GAME_STATUS.PLAYING,

    p1: {
      life: legacyState.p1Life,
      deck: legacyState.p1Deck,
      hand: legacyState.p1Hand,
      field: legacyState.p1Field,
      graveyard: legacyState.p1Graveyard,
      activeSP: legacyState.p1ActiveSP,
      restedSP: legacyState.p1RestedSP,
      fieldCard: legacyState.p1FieldCard,
      phaseCard: legacyState.p1PhaseCard,
      statusEffects: legacyState.p1StatusEffects || [],
      nextTurnSPBonus: legacyState.p1NextTurnSPBonus || 0,
      magicBlocked: legacyState.p1MagicBlocked || false,
      spReduction: legacyState.p1SpReduction || 0,
      bonusSP: 0,
      deepSeaZone: legacyState.p1DeepSeaZone || createDeepSeaZone(),
    },

    p2: {
      life: legacyState.p2Life,
      deck: legacyState.p2Deck,
      hand: legacyState.p2Hand,
      field: legacyState.p2Field,
      graveyard: legacyState.p2Graveyard,
      activeSP: legacyState.p2ActiveSP,
      restedSP: legacyState.p2RestedSP,
      fieldCard: legacyState.p2FieldCard,
      phaseCard: legacyState.p2PhaseCard,
      statusEffects: legacyState.p2StatusEffects || [],
      nextTurnSPBonus: legacyState.p2NextTurnSPBonus || 0,
      magicBlocked: legacyState.p2MagicBlocked || false,
      spReduction: legacyState.p2SpReduction || 0,
      bonusSP: 0,
      deepSeaZone: legacyState.p2DeepSeaZone || createDeepSeaZone(),
    },

    turnFlags: {
      chargeUsedThisTurn: legacyState.chargeUsedThisTurn || false,
      pendingOpponentSPRest: null,
    },

    triggers: {},
    logs: legacyState.logs || [],
    playedCardsHistory: {
      p1: [],
      p2: [],
    },
  };
}

export default useGameEngine;
